import React, { createContext, useContext } from 'react';

/**
 * TemplateConfigContext
 *
 * Provides the resolved template configuration (navigation, assets, features, theme,
 * routeExtensions) to any component in the unified app tree without prop-drilling.
 *
 * The context value is the object returned by resolveTemplate(skin) — see
 * src_unified/bootstrap/templateResolver.js.
 *
 * Usage:
 *   const { navigation, assets, features } = useTemplateConfig();
 *
 * Provided by: src_unified/bootstrap/index.js (TemplateConfigProvider wraps the app tree).
 * Consumed by: any unified component that needs skin-specific navigation/assets/features.
 */
const TemplateConfigContext = createContext(null);

export const TemplateConfigProvider = ({ config, children }) => (
    <TemplateConfigContext.Provider value={config}>
        {children}
    </TemplateConfigContext.Provider>
);

export const useTemplateConfig = () => {
    const ctx = useContext(TemplateConfigContext);
    if (!ctx) {
        throw new Error('useTemplateConfig must be used within a TemplateConfigProvider');
    }
    return ctx;
};

export const withTemplateConfig = (Component) => {
    return function WrappedComponent(props) {
        const templateConfig = useTemplateConfig();
        return <Component {...props} templateConfig={templateConfig} />;
    };
};

export default TemplateConfigContext;
